'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import type { Position } from '@/app/models/position';
import type { NewPositionRestriction } from '@/app/models/positionRestriction';
import { useSearchPositionsQuery } from '@/app/services/positionApi';
import { useCreatePositionRestrictionMutation } from '@/app/services/positionRestrictionApi';
import DrawerButtons from '@/components/common/DrawerButtons';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { Box, Typography, useTheme } from '@mui/material';

interface NewPositionRestrictionsProps {
  setDrawerOpen: (open: boolean) => void;
}

export default function NewPositionRestrictions(props: NewPositionRestrictionsProps) {
  const { t } = useTranslation(['positionRestriction', 'common']);

  const { setDrawerOpen } = props;
  const theme = useTheme();

  const [position, setPosition] = useState<Position | null>(null);
  const [positionSearch, setPositionSearch] = useState('');
  const { data: positions, isFetching: isLoadingPositions } = useSearchPositionsQuery(
    {
      searchTerm: positionSearch,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: positionSearch.length < 1 },
  );

  const [createSpecificAllocation] = useCreatePositionRestrictionMutation();

  const handleSubmit = async () => {
    if (!position) return;
    toast.info(t('new.value.sent'));
    const requestBody: NewPositionRestriction = {
      positionName: position.name,
    };
    await createSpecificAllocation(requestBody);
    toast.success(t('new.value.added'));
    setDrawerOpen(false);
  };

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('position.restriction')} subtitle={t('new.position.restriction.subtitle') as string} />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 4, // 32px
            gap: theme.spacing(2), // 16px
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 4 }}>
            <Typography component="p" variant="fieldHeader" sx={{ mt: 2 }}>
              {t('common:position')}
            </Typography>
            <GenericAutoComplete
              value={position}
              onChange={(value) => {
                setPosition(value);
              }}
              equalityTest={(option, value) => option?.id === value?.id}
              getOptionLabel={(option) => `${option?.name}`}
              onSearch={(value) => {
                setPositionSearch(value);
              }}
              options={positionSearch !== '' && positions?.content ? positions?.content : []}
              placeholder={t('search.position') || ''}
              loading={isLoadingPositions}
              sx={{ width: '100%' }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} disabled={!position} />
      </Box>
    </Box>
  );
}
